import {
  IOrderLine,
  IOrdersPagination,
} from '../../../graphQL/queries/getOrders/types'
import { IOrderDetail } from '../../../graphQL/queries/order/types'

/**
 * Page size used for every `getOrders` request unless a caller overrides it.
 * The repo carries no global default to inherit.
 * @internal
 */
export const DEFAULT_ORDERS_PAGE_SIZE = 10

/**
 * How long to wait between poll ticks while an order sits in `PROCESSING`.
 *
 * The fallback, not the value: a storefront overrides it with
 * `configuration.order.confirmation.pollingTime`, and this applies to every
 * storefront that has authored no such block — see
 * `utils/orderConfirmation.ts`.
 * @internal
 */
export const ORDER_POLL_INTERVAL_MS = 5000

/**
 * The smallest poll interval a storefront may configure, in milliseconds.
 *
 * A floor, not a default: `resolveOrderConfirmation` raises anything below it
 * up to this value rather than rejecting the block or reverting to
 * `ORDER_POLL_INTERVAL_MS`. An author who wrote a small number asked for
 * "fast", and the closest honourable answer is "as fast as we allow".
 *
 * It exists because `pollingTime` is milliseconds in a hand-authored remote
 * JSON block whose default reads `5000`, which makes
 * seconds-for-milliseconds the obvious slip: `pollingTime: 5` means five
 * seconds to the author and a 5 ms re-arm to the client. That is not a CPU
 * spin — every tick awaits a real round trip — but it polls as fast as the
 * API can answer for the whole budget. Since COM-871 the derived
 * `maxAttempts` bounds that run too — it is `ceil((timeout - first look) /
 * pollingTime)`, so a tiny interval buys proportionally more ticks rather
 * than unbounded ones, and the deadline is no longer the only thing that
 * ends the chain. It still polls as fast as this floor allows, which is why
 * the floor stays. Each tick costs a
 * `completeBundlesExtra` lookup server-side, so the cost lands on the API
 * rather than on the device.
 *
 * `1000` rather than something larger because it is the smallest value that
 * is plausibly *deliberate*: "poll every second" is a round number a tenant
 * might genuinely want, while anything under it in a milliseconds field is
 * far more likely a units slip or a typo. Raising the floor further would
 * start overriding legitimate configuration to buy comparatively little.
 *
 * Deliberately applies to `pollingTime` alone, and that decision weighed
 * **API cost only**. On that axis neither of the other two needs a floor: a
 * small `timeout` ends the poll *early*, which costs the API nothing, and
 * `maxAttempts` can only ever shorten a chain.
 *
 * **The shopper-visible axis is real, and COM-871 answered it without adding
 * a floor here.** `timeout: 300` did make every shopper see "outcome unknown"
 * on orders that confirmed perfectly normally, and an unreachable
 * `maxAttempts` was dead configuration nobody was told about. Neither got a
 * *floor*, because neither needed one:
 *
 * - `maxAttempts` is now **derived** from `timeout` and `pollingTime` by
 *   `derivePollAttempts`, which is both its default and its ceiling. There is
 *   no threshold to defend — the value is whatever the deadline affords.
 * - `timeout` is **rejected** when it affords no look at all, which lands on
 *   `ORDER_POLL_FIRST_LOOK_MS` because that is where the opening look is armed
 *   rather than by choice. Above that it is honoured however
 *   small, because "give up quickly" is a legitimate instruction in a way that
 *   "give up before looking" is not — and **clamped at
 *   `ORDER_POLL_MAX_TIMEOUT_MS`** at the far end, which is the one number
 *   COM-871 did have to choose. See that constant for the argument.
 *
 * So this constant is no longer the only bound whose value had to be argued
 * for, but it is still the only **floor**, and still `pollingTime`'s alone. It
 * is not an exoneration of the other two and never was — it is simply not the
 * place either of them is handled. See `utils/orderConfirmation.ts`.
 * @internal
 */
export const ORDER_POLL_MIN_INTERVAL_MS = 1000

/**
 * How long to wait before the **first** look at an order, in milliseconds.
 *
 * Not configurable, and not a poll interval: it is the delay on the one tick
 * that opens a chain, after which `pollingTime` governs every tick that
 * follows. Both poll sites used `pollingTime` for it, which made the spinner's
 * minimum duration one whole interval no matter how fast the server settled —
 * five seconds of spinner on `mondelez-mx-b2b-ng` staging for an order
 * confirmed in well under one (COM-875). The wait measured nothing.
 *
 * **What this trades, stated plainly, because "only the first look moves" is
 * not the whole truth.** The interval is untouched, but the tick *grid*
 * shifts: ticks land at `500, 500 + P, 500 + 2P` where they used to land at
 * `P, 2P, 3P` — every tick but the last, which COM-871 shortens to land on the
 * deadline rather than past it (`nextPollDelay`). An order settling inside the first 500ms is discovered up to
 * `P - 500` sooner; an order settling *after* it is discovered 500ms **later**
 * than before, because the opening look found nothing and the next one is a
 * full interval behind it. Against a uniform settle distribution that trade is
 * expected-value-neutral. It is worth making because the real distribution is
 * concentrated well under a second — that assumption is the entire basis of
 * this constant, and if it stops holding, a short backoff (500 -> 1000 ->
 * 2000 -> `pollingTime`) dominates a fixed delay across the whole range.
 *
 * **Not zero, and the reason is not politeness.** Three of the five sites
 * reach here having *just* read the order and seen it unsettled, so an
 * immediate re-read would ask the same question inside the same millisecond
 * and spend one of `maxAttempts` on an answer already in hand. The other two
 * — the confirmation hook's transport-anomaly and ambiguous-`catch` paths —
 * read nothing at all, and take the same delay only because the seam is one.
 *
 * **And it is a delay rather than a direct call because a `setTimeout` can be
 * cancelled before its request is ever sent.** Not because a direct call could
 * write back over a cleared slice: every `stopPolling()` caller bumps the
 * request token in the very next statement, so `pollTick`'s post-await
 * `isSuperseded` checks discard a stale write either way. What the handle buys
 * is the request that never happens — a cancelled timer costs no
 * `completeBundlesExtra` lookup and no attempt, where a direct call has spent
 * both before anything can stop it. It also keeps the property for a future
 * `stopPolling()` caller that does not happen to bump the token.
 *
 * `500` rather than something smaller because it is comfortably under the
 * `ORDER_POLL_MIN_INTERVAL_MS` floor above, which is what guarantees the first
 * look is never scheduled *later* than the cadence that follows it: the
 * smallest interval any storefront can configure is 1000, so no `Math.min`
 * against `pollingTime` is needed here and none is written — a clamp that can
 * never fire reads as a live guard. Changing `ORDER_POLL_FIRST_LOOK_MS` or
 * `ORDER_POLL_MIN_INTERVAL_MS` without the other breaks that relationship, and
 * `utils/tests/orderConfirmation.test.ts` fails when it does.
 *
 * **It acquired a second job in COM-871 and is now the poll's unit of
 * account.** `derivePollAttempts` measures the tick grid from here, so this is
 * the offset in the attempt bound *and* the boundary below which an authored
 * `timeout` buys no observation and is rejected. Nothing chose 500 for that
 * second role; it falls out of the first. Raising this constant therefore
 * widens the band of rejected timeouts as well as delaying the first look.
 * @internal
 */
export const ORDER_POLL_FIRST_LOOK_MS = 500

/**
 * How long to keep polling before giving up.
 *
 * A client-side deadline, distinct from the server's own
 * `ExternalRefStatus.TIMEOUT` verdict. Both can happen and they mean different
 * things — see `pollTimedOut` below.
 *
 * The fallback for `configuration.order.confirmation.timeout`, alongside
 * `ORDER_POLL_INTERVAL_MS`. There is still no constant for `maxAttempts`, but
 * the reason changed with COM-871: an unconfigured poll used to be bounded by
 * this deadline *alone*, and is now bounded by the attempt count
 * `derivePollAttempts` reads off this deadline and the interval. Both bounds
 * are therefore derived from this one number, and on the tick grid they fire on
 * the same tick — the closing look that lands on the deadline is the one that
 * spends the last attempt. Only latency separates them, and only in one
 * direction: slow requests spend the clock ahead of the grid, so the deadline
 * is what ends such a chain.
 *
 * It is also the value a `timeout` too small to afford a single look falls
 * back to, which is why it must stay comfortably above
 * `ORDER_POLL_FIRST_LOOK_MS`, and — since COM-871 — the base the ceiling
 * below is a multiple of.
 * @internal
 */
export const ORDER_POLL_TIMEOUT_MS = 60000

/**
 * The largest `timeout` a storefront may configure, in milliseconds.
 *
 * **A product judgement, and the only bound here that is one.**
 * `ORDER_POLL_FIRST_LOOK_MS` *is* the rejection floor — a budget at or below
 * the delay every chain's opening look is armed at buys nothing that lands
 * inside it — so nobody chose that boundary either. This one is chosen, and the
 * argument has to stand on its own.
 *
 * **Why a ceiling exists at all, given `maxAttempts` is derived.**
 * `pollTick`'s bound is `elapsed >= timeout || attempts >= maxAttempts`, an
 * **or**, so the tighter of the two ends the chain. Deriving `maxAttempts`
 * from `timeout` makes them agree by construction — and agreement is not
 * boundedness. Measured before this constant existed: `timeout: 86400000` with
 * `pollingTime: 1000` derived 86,401 attempts and a last tick on the deadline
 * itself. A day-long chain of authenticated `order` queries, each carrying a
 * `completeBundlesExtra` lookup server-side, for one abandoned checkout. The
 * derivation removed the *inconsistency* between the two bounds; only this
 * removes the absence of a ceiling.
 *
 * **Ten minutes, and why ten rather than three or thirty.** An order that has
 * not settled in ten minutes will not settle while the shopper is still
 * watching, so past that point the poll measures nothing anyone will see and
 * only spends lookups. Written as a multiple of `ORDER_POLL_TIMEOUT_MS` rather
 * than as `600000` so it is anchored to the shipped default instead of being a
 * free number: one order of magnitude above it accommodates an author who
 * genuinely needs longer, and two orders of magnitude is a mistake rather than
 * a requirement. Three would clamp a five-minute deadline, which is a
 * plausible deliberate value for a slow client system, so clamping would stop
 * being rare. Thirty would allow 1,800 requests at the interval floor, by
 * which point the webview has almost certainly been backgrounded — where
 * mobile timer throttling means the chain is not measuring what it claims to.
 *
 * **The clamp is self-instrumenting, which is what makes choosing a number
 * defensible at all.** It reports through `getOrderConfirmationConfigIssues`,
 * and the report names the authored value — so a tenant who genuinely needs
 * longer arrives in Datadog as a `timeout … clamped` naming a value just above
 * this one. If ten minutes is wrong we are told by the mechanism the clamp
 * already carries, rather than by a shopper.
 *
 * **Anchors that were rejected.** Nothing in cinnamon bounds a session, a cart
 * or a webview's lifetime, so there was no stronger anchor to prefer. The
 * storefront's session expiry is the nearest candidate and is unusable twice
 * over: it is conjunctive (`lastAccessAt` older than 60 minutes **and**
 * `startedAt` older than `configuration.sessionTtl`, which defaults to 1440),
 * so it bounds a shopper at about a day rather than at anything this poll
 * could usefully read. That leg is the whole of the rejection and it stands on
 * its own. A second leg said `sessionTtl` was not on cinnamon's wire and it was
 * simply **false** — `sessionFieldsQuery` selects it, and the storefront's user
 * schema has declared it since 2022. What is true is narrower and not an
 * argument for anything: it is absent from cinnamon's `SessionConfiguration`
 * TypeScript type, so reading it needs a type change and no query change at
 * all. A multiple of `ORDER_POLL_INTERVAL_MS` was rejected for a different
 * reason: a cadence times a count is the `maxAttempts` mistake again, not a
 * budget.
 *
 * **What changing it breaks.** `maxAttempts` is derived from `timeout`, so
 * this is the only bound on either — the worst-case chain is
 * `ceil((this - ORDER_POLL_FIRST_LOOK_MS) / ORDER_POLL_MIN_INTERVAL_MS) + 1`
 * requests, 601 today, and it grows linearly with this number. Lower it below
 * a value some storefront has already authored and that storefront's poll
 * shortens, silently apart from the report. It must stay above
 * `ORDER_POLL_TIMEOUT_MS`: a ceiling under the fallback would put an
 * unconfigured storefront outside its own policy, and
 * `utils/tests/orderConfirmation.test.ts` fails when it does.
 * @internal
 */
export const ORDER_POLL_MAX_TIMEOUT_MS = ORDER_POLL_TIMEOUT_MS * 10

/**
 * List-level fetch state.
 *
 * These four literals are the `OrderListStatus` contract published by
 * `OrderList` in `@engyalo/delivery-ui-components`. The union is kept flat on
 * purpose — see `isLoadingMore` on `IOrdersState` for why load-more does not
 * add a fifth member.
 * @internal
 */
export type OrdersStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Detail-level fetch state.
 *
 * A superset of `OrdersStatus`, because the detail screen has two outcomes the
 * list has no analogue for. `OrderDetailPanel` publishes both as members of its
 * own `OrderDetailStatus` and renders each with its own message, so they belong
 * in the status rather than in flags beside it — a separate boolean could only
 * be read by comparing it against the status anyway.
 *
 * `OrdersStatus` itself is deliberately not widened: it is `OrderList`'s
 * published `OrderListStatus` contract, and adding a member the component does
 * not know would be a lie about what the list can render.
 * @internal
 */
export type OrderDetailStatus = OrdersStatus | 'notFound' | 'sessionEnded'

/**
 * Where the in-webview order-creation flow stands.
 *
 * A separate union from `OrderDetailStatus`, not a widening of it, because the
 * two describe different things: that one is how a *read* of an existing order
 * went, this one is how far a *write* has got. An order that is `notFound` and
 * one that is `confirming` have no common scale to sit on.
 *
 * The three terminal members — `created`, `confirmed`, `failed` — are named to
 * match `OrderOutcome` in `utils/orderConfirmation.ts`, which is what derives
 * them, so the flow dispatches its verdict without a translation table. The
 * other three are properties of the flow rather than of the order:
 *
 * - `creating` — `createOrder` is in flight. No order exists yet, and nothing
 *   the shopper does can be undone by leaving.
 * - `confirming` — `confirmOrder` is in flight, or the poll is running. The
 *   order exists either way. One member covers both because the shopper is
 *   told the same thing and can do the same thing (nothing) in both.
 * - `unknown` — the poll spent its budget with the order still unsettled.
 *   Deliberately **not** `failed`: the order exists, `createOrder` returned it,
 *   and only the confirmation outcome is unknown. Announcing a failure here
 *   would tell a shopper their order did not happen when it very likely did.
 * @internal
 */
export type OrderCreationStatus =
  | 'idle'
  | 'creating'
  | 'confirming'
  | 'created'
  | 'confirmed'
  | 'failed'
  | 'unknown'

/**
 * Why a creation or confirmation attempt failed, for the screen to route on.
 *
 * Not an error object: the flow classifies the failure once, in its catch, and
 * the slice stores the classification. `message` is the server's own text
 * where it carries the reason a shopper needs — the client system's decline is
 * the case that matters — and `failedCheckoutRules` is the machine-readable
 * half, empty for everything that is not a rule rejection.
 * @internal
 */
export interface IOrderCreationFailure {
  message?: string
  failedCheckoutRules?: string[]
  /**
   * The order the verdict is about, where one exists.
   *
   * Carried because a failure is not always the absence of an order. A poll
   * tick that reads a configured failure status has the order in hand and it
   * is the one the screen must render — without this the slice would hold the
   * *previous* tick's copy, so `creationStatus: 'failed'` would sit beside a
   * `created.status` still reading `CREATED`. A creation failure omits it,
   * because nothing was created.
   */
  order?: IOrderDetail
}

/**
 * One history row, shaped for `OrderCard` / `OrderList`.
 *
 * Structurally the library's `OrderSummary`, declared here rather than
 * imported because the components are still unreleased. `itemsCount` is
 * derived client-side; `currency` is absent by design and read from
 * `config.options.currencyPrefix` at render time.
 * @internal
 */
export interface IOrderSummary {
  id: string
  createdAt?: string
  itemsCount?: number
  /**
   * The order's lines, kept so reorder can send them to `prefillCart` without
   * a second request. `OrderCard` and `OrderList` do not declare this prop
   * and ignore it.
   */
  items?: IOrderLine[]
  total?: number
  status?: string
  sequence?: number
}

/**
 * A resolved depot, kept beside the code it was looked up under.
 *
 * Both halves are required. A resolution that produced no usable name is not
 * represented here at all — it is the absence of the whole record — because
 * `{ code, name: '' }` and "nothing resolved" would then be two ways to say
 * the same thing and a reader would have to guard both.
 * @internal
 */
export interface IOrderDistributor {
  /**
   * The `Order.storeCode` this name was resolved from, trimmed.
   *
   * Trimmed because it is compared against a trimmed `selected.storeCode` at
   * read time and the two must agree; and because a code is an identifier
   * rather than copy, so surrounding whitespace carries no meaning. Contrast
   * `name`, which is stored verbatim.
   */
  code: string
  /** The depot's display name, exactly as `getStores` returned it. */
  name: string
}

export interface IOrdersState {
  list: IOrderSummary[]
  pagination: IOrdersPagination
  /**
   * Selected status filter. An empty string means no filter.
   */
  activeFilter: string
  status: OrdersStatus
  /**
   * True while a load-more page is in flight.
   *
   * `OrderList` computes `showRows = !isLoading && !isError && rows.length > 0`
   * and mounts the load-more sentinel only when `showRows` is true. Setting
   * `status: 'loading'` to fetch page 2 would therefore swap the rows for
   * skeletons and unmount the sentinel mid-scroll. So `status` holds `'ready'`
   * through pagination and the in-flight page is tracked here instead. This
   * doubles as the re-entrancy guard that stops a repeated sentinel fire from
   * requesting the same page twice.
   */
  isLoadingMore: boolean
  /**
   * Why the last load-more failed, or null.
   *
   * An append failure must not discard the pages already on screen, and
   * `status: 'error'` hides them. So a failed page 3 leaves `status: 'ready'`
   * and records the reason here for the container to surface as a toast or an
   * inline retry. Only a first-page failure sets `status: 'error'`.
   */
  loadMoreError: string | null
  /**
   * The order on the Detail screen, or null when none is loaded.
   *
   * The detail half is deliberately flat siblings of the list half rather than
   * a nested `selected: { ... }` namespace, so the slice reads with one
   * convention throughout. Each field below is the mirror of a list field, and
   * mirrors its reasoning as well as its name.
   */
  selected: IOrderDetail | null
  /**
   * Mirrors `status`, for the detail fetch, over the wider union.
   *
   * `'notFound'` is its own member rather than `'error'` plus a flag: the
   * server answers "no such order" and "not yours" with the same 404 on
   * purpose, so that a probe cannot confirm an id names a real order, and this
   * value inherits exactly that — "not available to you", nothing narrower.
   *
   * `'sessionEnded'` is the shopper's link having outlived its session. It is
   * set without a request going out; see the guard in `useGetOrderDetail`.
   */
  selectedStatus: OrderDetailStatus
  /**
   * The depot name resolved for the selected order, paired with the code it
   * was resolved *from*, or null when nothing has resolved (COM-889).
   *
   * **The pair is the point.** A bare name would be a value with no owner, and
   * two ordering hazards would come back as lifecycle problems: a name
   * resolved for order A must not render under order B, and a poll tick that
   * replaces `selected` must not blank a name that is still correct. Carrying
   * the code turns both into one comparison at read time —
   * `selectOrderDistributor` renders the name only where the code still
   * matches `selected.storeCode` — instead of a set of clears spread across
   * reducers that would each have to be got right.
   *
   * A third hazard, a lookup resolving after the shopper has moved on, belongs
   * to `requestIdRef` in `useOrderDistributor` and **not** to this pair: the
   * comparison would hide the stale row, but the stale write would still have
   * landed. The comparison stops a wrong row; the token stops a lost one.
   *
   * That comparison is also what buys the row being on screen at the *first
   * paint* of a re-read order. `setOrderDetailLoading` blanks `selected`, so
   * the row is off screen during the re-read whether or not the distributor is
   * cleared with it — the flicker is not what is avoided. What not-clearing
   * buys is that the restored order paints *with* its depot rather than
   * gaining it a round trip later, which is the difference between a row that
   * is simply there and one that visibly arrives.
   *
   * There is no `'loading'` member and no error member, deliberately. A depot
   * name is either known or it is not: unresolved, missed, and failed are one
   * state as far as this screen is concerned, and that state is "no row". The
   * panel is given `null` and draws nothing — no placeholder, no spinner, and
   * never the raw `storeCode`, which would put `'5887'` under a heading
   * reading "Distribuidor".
   *
   * `name` is stored as the server sent it, untrimmed. The library trims for
   * display and treats blank and whitespace-only as absent, so normalising
   * here would only hide from a future reader that the live values have a
   * leading space.
   */
  selectedDistributor: IOrderDistributor | null
  /**
   * True while a poll tick is in flight.
   *
   * The counterpart of `isLoadingMore` for display purposes only. Flipping
   * `selectedStatus` to `'loading'` every few seconds would blank an
   * already-rendered order and replace it with skeletons on every tick, so
   * `selectedStatus` holds `'ready'` throughout and the in-flight tick is
   * tracked here for a subtle pending affordance.
   *
   * Unlike `isLoadingMore` it is **not** a re-entrancy guard, and nothing
   * reads it to decide whether to fetch. Overlapping ticks are prevented in
   * `useGetOrderDetail` by the single-timer chain in `pollTimerRef`: a tick is
   * scheduled only once the previous one has resolved. Do not add a guard here
   * on the strength of the `isLoadingMore` analogy — the mechanisms differ.
   */
  isPollingOrder: boolean
  /**
   * Why the last poll tick failed, or null.
   *
   * The counterpart of `loadMoreError`: one failed tick must not discard the
   * order already on screen, and `selectedStatus: 'error'` would. So a failed
   * tick leaves `selectedStatus: 'ready'` and records the reason here. Only
   * the initial fetch sets `selectedStatus: 'error'`.
   */
  pollError: string | null
  /**
   * True once polling gave up on an order still reported as `PROCESSING`.
   *
   * A client-side deadline, and not the same fact as
   * `selected.externalRefStatus === 'TIMEOUT'`, which is the server's own
   * verdict about the downstream system. Either can occur without the other,
   * so they are two fields: one boolean could not say which happened.
   */
  pollTimedOut: boolean
  /**
   * Bumped once per refresh request from the Detail screen.
   *
   * A counter rather than a boolean because the value means nothing and only
   * its *change* is the signal: two refreshes in a row are two distinct edges,
   * where a flag already `true` would be none. Monotonic and never reset —
   * `clearOrderDetail` deliberately leaves it alone — since a reset is itself
   * a change and would read as a refresh to anything watching it.
   *
   * It exists because re-fetching is the one Detail action that cannot be
   * expressed as a plain reducer, and the usual seam does not work for it. An
   * `EventManager` handler that called `useGetOrderDetail` would hold a
   * *second instance* of that hook, whose poll timer and request token live in
   * `useRef` and are therefore per instance — so it would arm its own chain
   * against this slice rather than supersede the live one, both writing
   * `selected` on their own timers. That is COM-741's defect class. A reset
   * never needed this: it *is* a reducer, so it is a plain `clearOrderDetail`
   * dispatch (from `useOrderDetail`'s unmount cleanup), but a fetch is not.
   *
   * A dispatch crosses instances where a ref does not, so the request is put
   * in the store and the one instance that owns the timer picks it up. See the
   * effect in `useGetOrderDetail`, which is also where the guard lives that
   * stops an instance which has fetched nothing from fetching on the edge.
   */
  detailRefreshNonce: number
  /**
   * Bumped once per refresh request from the Confirmation screen.
   *
   * **The canonical account of how a refresh crosses hook instances lives
   * here.** `useOrderConfirmation`, `useOrderActions`,
   * `templates/OrderConfirmation.tsx` and the suites all point at this note
   * rather than restating it; eight independently-worded copies of one
   * rationale is the drift the repo has been bitten by before.
   *
   * A counter for the reason `detailRefreshNonce` is one, and a *second*
   * counter rather than a share of that one because the two screens are
   * independent: an order can be open on Detail while the write half sits at
   * `unknown`. Sharing a field would not make either screen read the other's
   * order — each hook re-reads its own — it would make one screen's refresh
   * fire a **spurious read on the other**, costing a request and, on Detail,
   * blanking a rendered order for a tick.
   *
   * The instance-crossing problem is the Detail screen's, with one difference
   * that changes what the nonce can promise. There, the instance that owns the
   * poll (`useGetOrderDetail`, mounted by the template) is not the one an
   * `EventManager` handler holds, and the guard on the edge is *ownership*: an
   * instance that has fetched nothing ignores it. Here **no mounted instance
   * owns the exhausted attempt at all** — the chain that spent its budget
   * belonged to the cart's `EventManager`, which `<Routes>` unmounted on the
   * way to this screen — so a refresh is a new read begun by whichever
   * instance is mounted, against the order in `created`. The guard that stands
   * in for ownership is therefore `creationStatus === 'unknown'`, the only
   * state the panel offers the control on.
   *
   * That is sound while exactly one `useOrderConfirmation` is mounted, and
   * today exactly one is: `<Routes>` mounts one container at a time, and each
   * container mounts one `EventManager` — `templates/Default.tsx` for every
   * template that delegates to it, and `templates/CatalogFixed.tsx`, which
   * renders its own rather than delegating. **A property of today's call
   * sites, not an invariant anything enforces**: nothing stops a future
   * template from mounting a second one, and two instances would both act on
   * the same edge and open two chains against one order. If that changes, this
   * needs an ownership test of its own rather than a status guard.
   *
   * Monotonic and never reset, like its sibling: `clearOrderCreation` leaves it
   * alone, because writing `0` over it is itself a change and would read as a
   * refresh request to the effect watching it.
   */
  confirmationRefreshNonce: number
  /**
   * How far the in-webview order-creation flow has got.
   *
   * The write half of this slice, kept here rather than in a slice of its own
   * because it is the same entity: the order the flow creates is the order the
   * poll re-reads and the screen renders, and `created` below is written from
   * both the mutation responses and `order` query ticks. A second slice would
   * have to mirror the read half to do that.
   *
   * `'idle'` for every storefront that has not authored
   * `options.inWebviewOrderConfirmation` — which is all of them until one
   * does, and is what makes the write half inert by default.
   */
  creationStatus: OrderCreationStatus
  /**
   * The order the flow created, or null before it exists.
   *
   * Distinct from `selected`, which is the order the *Detail screen* is
   * reading. Nothing stops a shopper reaching Order Detail later in the same
   * session, and folding the two together would mean opening a history row
   * overwrote the confirmation screen's order — or worse, that a stale
   * confirmation order rendered under a detail route.
   */
  created: IOrderDetail | null
  /**
   * The reason the flow failed, or null.
   *
   * Set only on the way into `'failed'`. The server's own message where it
   * carries the reason — a client-system decline says "order was not
   * confirmed" and that is the one thing worth showing — and null otherwise,
   * so the screen falls back to its configured copy rather than printing
   * `BAD REQUEST - …` at a shopper.
   */
  creationError: string | null
  /**
   * Checkout rules the order was rejected for, or empty.
   *
   * The `extras` of the server's `BAD_REQUEST`, filtered to members of the
   * `CheckoutRules` enum. Empty is the normal case and means "not a rule
   * rejection", which is why this is a list rather than a nullable one: the
   * screen asks whether there is anything to route to, and `[]` answers that
   * without a null check.
   *
   * Note the enforcement behind it is still fail-open in production until
   * COM-736 step 2 lands, so this is correct-but-rarely-populated today.
   */
  failedCheckoutRules: string[]
}

export interface OrdersPagePayload {
  orders: IOrderSummary[]
  pagination?: IOrdersPagination | null
}
